import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "../../../../Utils/axiosInstance";
import MapPicker from "../../../Auth/Component/MapPicker";
import { useLockBodyScroll } from "../../../Hooks/useLockBodyScroll";

const AddAddressModalAPI = ({
  showModal,
  closeModal,
  onBack,
  refreshAddresses,
  type = "Office",
  editData = null,
  addresses = [],
}) => {
  const [animate, setAnimate] = useState(false);
  const [loading, setLoading] = useState(false);
  useLockBodyScroll(showModal);

  const emptyForm = {
    addressType: type,
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    lat: null,
    lng: null,
    isDefault: false,
  };

  const [form, setForm] = useState(emptyForm);

  // Reset form when opening for a new address (no editData)
  useEffect(() => {
    if (showModal && !editData) {
      setForm({ ...emptyForm, addressType: type });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, type, editData]);

  // Sync addressType when type prop changes (while modal is open)
  useEffect(() => {
    if (showModal) {
      setForm((prev) => ({ ...prev, addressType: type }));
    }
  }, [type, showModal]);

  // Prefill for edit mode
  useEffect(() => {
    if (editData) {
      setForm({
        ...editData,
        addressType: editData.addressType || type,
        addressLine2: editData.addressLine2 || "",
      });
    }
  }, [editData, type]);

  // Animate in
  useEffect(() => {
    if (showModal) setTimeout(() => setAnimate(true), 10);
  }, [showModal]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(() => {
      if (onBack) onBack();
      else closeModal();
    }, 250);
  };

  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const payload = {
        ...form,
        addressType: type,
        country: "India",
      };

      if (editData?._id) {
        await axios.put(`/address/${editData._id}`, payload);
      } else {
        await axios.post("/address", payload);
      }

      await refreshAddresses();
      handleClose();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // "Same as Office" — only shown when adding/editing a WareHouse address
  const handleSameAsOffice = () => {
    const office = addresses.find((a) => a.addressType === "Office");
    if (!office) return;
    const { _id, createdAt, updatedAt, isDefault, ...cleanOffice } = office;
    setForm({
      ...cleanOffice,
      addressType: "WareHouse",
      isDefault: false,
    });
  };

  if (!showModal) return null;

  const isWarehouse = type === "WareHouse";
  const hasOfficeAddress = addresses.some((a) => a.addressType === "Office");

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
      bg-black/30 backdrop-blur-sm transition-opacity duration-300
      ${animate ? "opacity-100" : "opacity-0"}`}
    >
      <div
        className={`bg-white w-full md:w-[520px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden
        ${animate
            ? "scale-100 translate-y-0"
            : "scale-95 md:translate-y-4 translate-y-full"
          }`}
        style={{ maxHeight: "90vh" }}
      >
        {/* HEADER */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center flex-shrink-0">
          <button onClick={handleClose} className="text-2xl leading-none">
            ←
          </button>
          <div className="flex-1 text-center font-semibold text-lg">
            {editData ? "Edit" : "Add"}{" "}
            {type === "WareHouse" ? "Warehouse" : type} Address
          </div>
        </div>

        {/* BODY */}
        <div className="p-5 space-y-4 overflow-auto flex-1">
          {/* MAP PICKER */}
          <MapPicker
            apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
            lat={form.lat}
            lng={form.lng}
            onAddressChange={(addr) =>
              setForm((prev) => ({
                ...prev,
                addressLine1: addr.addressLine1 || prev.addressLine1,
                city: addr.city || prev.city,
                state: addr.state || prev.state,
                pincode: addr.pincode || prev.pincode,
                lat: addr.lat,
                lng: addr.lng,
              }))
            }
          />

          {/* SAME AS OFFICE — only for WareHouse when an office exists */}
          {isWarehouse && hasOfficeAddress && (
            <button
              onClick={handleSameAsOffice}
              className="w-full border border-green-500 text-green-600 py-2 rounded-xl text-sm font-medium hover:bg-green-50 transition-colors"
            >
              📍 Same as Office Location
            </button>
          )}

          {/* ADDRESS LINE 1 — auto-filled by map */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">
              Address Line 1 <span className="text-red-400">*</span>
            </label>
            <input
              placeholder="Street, building name (auto-filled from map)"
              value={form.addressLine1}
              onChange={handleChange("addressLine1")}
              className="w-full border p-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor transition"
            />
          </div>

          {/* LANDMARKS — manual entry */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">
              Landmark <span className="text-gray-300 font-normal">(optional)</span>
            </label>
            <input
              placeholder="Flat no., floor, wing, landmark…"
              value={form.addressLine2}
              onChange={handleChange("addressLine2")}
              className="w-full border p-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor transition"
            />
          </div>

          {/* CITY / STATE / PINCODE */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">
                City <span className="text-red-400">*</span>
              </label>
              <input
                placeholder="City"
                value={form.city}
                onChange={handleChange("city")}
                className="w-full border p-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor transition"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">
                State <span className="text-red-400">*</span>
              </label>
              <input
                placeholder="State"
                value={form.state}
                onChange={handleChange("state")}
                className="w-full border p-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor transition"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1 ml-1">
              Pincode <span className="text-red-400">*</span>
            </label>
            <input
              placeholder="6-digit pincode"
              value={form.pincode}
              onChange={handleChange("pincode")}
              maxLength={6}
              className="w-full border p-3 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primaryColor/30 focus:border-primaryColor transition"
            />
          </div>
        </div>

        {/* FOOTER */}
        <div className="p-5 border-t flex gap-3 flex-shrink-0">
          <button
            onClick={handleClose}
            className="flex-1 py-3 border rounded-xl text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 py-3 bg-primaryColor text-white rounded-xl text-sm font-medium hover:bg-primaryColor/90 transition-colors disabled:opacity-70"
          >
            {loading ? "Saving…" : editData ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AddAddressModalAPI;
